import React, { useEffect, useMemo, useState } from "react";
import {
  Filter,
  Search,
  History,
  Building,
  Star,
  UserCheck,
  BarChart3,
  Mail,
  Phone,
  Code,
  Zap,
  Loader,
  Users,
  RefreshCw,
  Clock,
  X,
} from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import EmployeeAvatarCell from "../../../components/EmployeeAvatarCell";
import MobileFilterToggle from "../../../components/MobileFilterToggle";

const EmployeeTable = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  isLoading,
  employees,
  filteredEmployees,
  clients,
  companies,
  departments,
  performances,
  statuses,
  userRole,
  selectedClient,
  setSelectedClient,
  filterCompany,
  setFilterCompany,
  searchTerm,
  setSearchTerm,
  filterDepartment,
  setFilterDepartment,
  filterPerformance,
  setFilterPerformance,
  filterStatus,
  setFilterStatus,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  employeeImages,
  hoveredActionBtn,
  setHoveredActionBtn,
  getEmployeeName,
  getInitials,
  hasFeatureAccess,
  isColumnVisible,
  canViewPendingApprovals,
  onOpenHistory,
  onOpenPending,
  onAction,
  onHistory,
  onClearFilters,
}) => {
  // Page action buttons in the system style (same as the Payroll page:
  // filled #2e7cba primary, neutral outlined secondary, no shadow).
  const systemButton = (variant) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.65rem 1.1rem",
    borderRadius: "0.85rem",
    fontSize: "0.92rem",
    fontWeight: variant === "primary" ? 700 : 600,
    fontFamily: "inherit",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    ...(variant === "primary"
      ? { background: "#2e7cba", color: "#ffffff", border: "1px solid #2e7cba" }
      : {
          background: theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
          color: theme === "dark" ? "#ffffff" : "#1a1a1a",
          border: theme === "dark" ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.1)",
        }),
  });

  // Column visibility (opt-out: default visible, hidden only when the admin
  // level's hiddenColumns explicitly lists the column) + a dedicated feature
  // for the "Employee Action" (Zap) button — previously unconditional, so
  // there was no way to hide it from an admin level at all.
  const showEmployeeColumn = !isColumnVisible || isColumnVisible("changesPage", "employeeActionsTable", "employee");
  const showActionsColumn = !isColumnVisible || isColumnVisible("changesPage", "employeeActionsTable", "actions");
  const canPerformAction = !hasFeatureAccess || hasFeatureAccess("changesPage", "performaction");

  // ── Pagination ──────────────────────────────────────────────────────────
  // This table used to render every row of `filteredEmployees` at once —
  // for a client with hundreds/thousands of employees that meant the full
  // set mounted (each row carrying an avatar cell + two action buttons with
  // hover tooltips) on every load and on every keystroke in the search box.
  // The data is already fetched and filtered client-side, so this is a
  // free client-side slice: only a page's worth is ever in the DOM, and
  // "Show more" grows it without touching the network.
  const CHANGES_PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(CHANGES_PAGE_SIZE);
  // Phones: company/department/status fold behind a "Filters" button.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Reset to the first page whenever the result set changes underneath —
  // new search/filter/client selection, or a refetch after an
  // approve/reject that changes which employees show up.
  useEffect(() => {
    setVisibleCount(CHANGES_PAGE_SIZE);
  }, [searchTerm, filterCompany, filterDepartment, filterPerformance, filterStatus, selectedClient, employees]);

  const pagedEmployees = useMemo(
    () => filteredEmployees.slice(0, visibleCount),
    [filteredEmployees, visibleCount]
  );
  const hasMore = visibleCount < filteredEmployees.length;

  return (
    <>
      {/* Filter Section */}
      <div style={styles.filterCard}>
        <div
          style={{
            display: "flex",
            flexDirection: isRTL ? "row" : "row",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "clamp(22px, 3.5vw, 28px)",
            paddingBottom: "clamp(18px, 2.8vw, 22px)",
            borderBottom:
              theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.1)",
            flexWrap: "wrap",
            gap: "18px",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "clamp(18px, 3.2vw, 22px)",
                fontWeight: "900",
                color: theme === "dark" ? "#ffffff" : "#0f172a",
                margin: "0 0 8px 0",
                letterSpacing: "-0.02em",
                display: "flex",
                flexDirection: isRTL ? "row" : "row",
                alignItems: "center",
                gap: "clamp(10px, 1.5vw, 12px)",
              }}
            >
              <Filter size={window.innerWidth < 768 ? 20 : 22} />
              {t.searchFilterEmployees}
              {/* Active filters badge */}
              {(() => {
                const count = [searchTerm, filterCompany !== "all" && filterCompany, filterDepartment !== "all" && filterDepartment, filterPerformance !== "all" && filterPerformance, filterStatus !== "all" && filterStatus].filter(Boolean).length;
                return count > 0 ? (
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "24px",
                    height: "24px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "700",
                    padding: "0 7px",
                  }}>{count}</span>
                ) : null;
              })()}
            </h3>
            <p
              style={{
                fontSize: "clamp(12px, 1.7vw, 14px)",
                color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                margin: 0,
                fontWeight: "600",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t.showing} {Math.min(visibleCount, filteredEmployees.length)} {t.of} {filteredEmployees.length}{" "}
              {t.employees}
              {filteredEmployees.length !== employees.length &&
                ` (${employees.length} ${t.total || "total"})`}
              {(userRole === "Admin" || userRole === "admin") &&
                selectedClient !== "all" &&
                ` ${t.forSelectedClient}`}
            </p>
          </div>
          <MobileFilterToggle
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
            activeCount={[filterCompany, filterDepartment, filterPerformance, filterStatus].filter((v) => v && v !== "all").length}
            language={language}
            theme={theme}
          />
          {/* Clear All Filters button */}
          {(() => {
            const count = [searchTerm, filterCompany !== "all" && filterCompany, filterDepartment !== "all" && filterDepartment, filterPerformance !== "all" && filterPerformance, filterStatus !== "all" && filterStatus].filter(Boolean).length;
            return count > 0 ? (
              <button
                onClick={onClearFilters}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "10px",
                  border: "none",
                  background: theme === "dark" ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)",
                  color: "#ef4444",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <X size={14} />
                {language === "ar" ? "مسح الكل" : "Clear All"} ({count})
              </button>
            ) : null;
          })()}
        </div>

        <div className="nx-filter-grid" data-open={String(filtersOpen)} style={styles.controlsGrid}>
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Search size={15} />
              {t.searchEmployee}
            </label>
            <div style={{ position: "relative" }}>
              <div style={{
                position: "absolute",
                [isRTL ? "right" : "left"]: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#94a3b8",
                zIndex: 1,
              }}>
                <Search size={18} />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t.searchPlaceholder}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: "48px",
                  borderRadius: "12px",
                  border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.3)" : "rgba(148,163,184,0.35)"}`,
                  background: theme === "dark" ? "rgba(71,85,105,0.4)" : "rgba(226,232,240,0.6)",
                  color: theme === "dark" ? "#f1f5f9" : "#334155",
                  fontSize: "14px",
                  fontWeight: "500",
                  outline: "none",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  textAlign: isRTL ? "right" : "left",
                  direction: isRTL ? "rtl" : "ltr",
                  paddingLeft: isRTL ? (searchTerm ? "42px" : "16px") : "52px",
                  paddingRight: isRTL ? "52px" : (searchTerm ? "42px" : "16px"),
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    [isRTL ? "left" : "right"]: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                    border: "none",
                    borderRadius: "50%",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#64748b",
                    padding: 0,
                    zIndex: 2,
                  }}
                  title={language === "ar" ? "مسح" : "Clear"}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>



          <div className="nx-filter-extra" style={styles.controlGroup}>
            <label style={styles.label}>
              <Building size={15} />
              {t.company || (language === "ar" ? "الشركة" : "Company")}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: "all", name: t.allCompanies || (language === "ar" ? "جميع الشركات" : "All Companies") },
                  ...(companies || []).map((c) => ({ id: c, name: c })),
                ]}
                value={filterCompany}
                onChange={(val) => setFilterCompany(val || "all")}
                onSelect={(item) => {
                  const value = typeof item === "string" ? item : item?.id || String(item);
                  setFilterCompany(value);
                }}
                getItemKey={(it) => typeof it === "string" ? it : it.id || String(it)}
                getItemLabel={(it) => typeof it === "string" ? it : it.name || String(it)}
                placeholder={t.selectCompany || (language === "ar" ? "اختر الشركة..." : "Select Company...")}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => setFilterCompany("all")}
              />
            </div>
          </div>

          <div className="nx-filter-extra" style={styles.controlGroup}>
            <label style={styles.label}>
              <Building size={15} />
              {t.department}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: "all", name: t.allDepartments || "All Departments" },
                  ...departments.map((dept) => ({ id: dept, name: dept })),
                ]}
                value={filterDepartment}
                onChange={(val) => setFilterDepartment(val || "all")}
                onSelect={(item) => {
                  const value =
                    typeof item === "string" ? item : item?.id || String(item);
                  setFilterDepartment(value);
                }}
                getItemKey={(it) =>
                  typeof it === "string" ? it : it.id || String(it)
                }
                getItemLabel={(it) =>
                  typeof it === "string" ? it : it.name || String(it)
                }
                placeholder={t.selectDepartment || "اختر قسم..."}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => setFilterDepartment("all")}
              />
            </div>
          </div>

          <div className="nx-filter-extra" style={styles.controlGroup}>
            <label style={styles.label}>
              <UserCheck size={15} />
              {t.status}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: "all", name: t.allStatuses || "All" },
                  ...statuses.map((status) => ({ id: status, name: status })),
                ]}
                value={filterStatus}
                onChange={(val) => setFilterStatus(val || "all")}
                onSelect={(item) => {
                  const value =
                    typeof item === "string" ? item : item?.id || String(item);
                  setFilterStatus(value);
                }}
                getItemKey={(it) =>
                  typeof it === "string" ? it : it.id || String(it)
                }
                getItemLabel={(it) =>
                  typeof it === "string" ? it : it.name || String(it)
                }
                placeholder={t.selectStatus || "اختر الحالة..."}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => setFilterStatus("all")}
              />
            </div>
          </div>

          {/* <div className="nx-filter-extra" style={styles.controlGroup}>
            <label style={styles.label}>
              <BarChart3 size={15} />
              {t.sortBy}
            </label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <div style={{ flex: 1, position: "relative" }}>
                <ReusableDropdown
                  items={[
                    { id: "name", name: t.name },
                    { id: "salary", name: t.salary },
                    { id: "joinDate", name: t.joinDate },
                    { id: "performance", name: t.performance },
                  ]}
                  value={sortBy}
                  onChange={(val) => setSortBy(val || "name")}
                  onSelect={(item) => setSortBy(item.id)}
                  getItemKey={(it) =>
                    typeof it === "string" ? it : it.id || String(it)
                  }
                  getItemLabel={(it) =>
                    typeof it === "string" ? it : it.name || String(it)
                  }
                  placeholder={t.sortBy}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => setSortBy("name")}
                />
              </div>
              <button
                style={{
                  ...styles.select,
                  fontSize: "clamp(20px, 3vw, 24px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                title={sortOrder === "asc" ? t.ascendingOrder : t.descendingOrder}
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div> */}

        </div>

        {/* Action Buttons — below the filters */}
        {((hasFeatureAccess && hasFeatureAccess("changesPage", "viewhistory")) ||
          canViewPendingApprovals) && (
          <div
            className="nx-actions-row nx-actions-row--single"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
              flexWrap: "nowrap",
              marginTop: "20px",
              paddingTop: "24px",
              borderTop:
                theme === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {hasFeatureAccess && hasFeatureAccess("changesPage", "viewhistory") && (
              <button
                type="button"
                style={systemButton("primary")}
                onClick={onOpenHistory}
              >
                <History size={16} /> {t.viewHistory}
              </button>
            )}
            {onOpenPending && canViewPendingApprovals && (
              <button
                type="button"
                style={systemButton("secondary")}
                onClick={onOpenPending}
              >
                <Clock size={16} /> {t.pendingApprovals || "Pending Approvals"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Employee Table */}
      <div style={styles.tableCard}>
        {isLoading && (
          <div style={styles.loadingOverlay}>
            <div style={{ textAlign: "center" }}>
              <Loader size={48} style={styles.loadingSpinner} color="#2e7cba" />
              <p
                style={{
                  marginTop: "16px",
                  fontWeight: "600",
                  color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
                }}
              >
                {t.loadingEmployees}
              </p>
            </div>
          </div>
        )}

        <div style={styles.tableWrapper}>
          {filteredEmployees.length > 0 ? (
            <>
            <table className="nx-fit-table" style={styles.table}>
              <thead>
                <tr>
                  {showEmployeeColumn && <th style={styles.th}>{t.employee}</th>}
                  {showActionsColumn && <th style={styles.th}>{t.actions}</th>}
                </tr>
              </thead>
              <tbody>
                {pagedEmployees.map((emp) => (
                  <tr key={emp.id || emp.infinityCode}>
                    {showEmployeeColumn && (
                      <td style={{ ...styles.td, textAlign: "left", padding: 0 }}>
                        <div style={{ padding: "16px 12px" }}>
                          <EmployeeAvatarCell
                            employee={emp}
                            profilePic={employeeImages[emp.infinityCode]}
                            language={language}
                            theme={theme}
                            getEmployeeName={getEmployeeName}
                          />
                        </div>
                      </td>
                    )}

                    {showActionsColumn && (
                    <td style={styles.td}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: isRTL ? "row" : "row",
                          gap: "8px",
                          flexWrap: "nowrap",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        {canPerformAction && (
                        <button
                          style={{
                            ...styles.actionButton,
                            background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                            minWidth: "fit-content",
                          }}
                          onClick={() => onAction(emp)}
                          onMouseEnter={() => setHoveredActionBtn(emp.infinityCode)}
                          onMouseLeave={() => setHoveredActionBtn(null)}
                        >
                          <Zap size={16} />
                        </button>
                        )}
                        {hoveredActionBtn === emp.infinityCode && (
                          <span
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              marginTop: "4px",
                              background: "rgba(0,0,0,0.75)",
                              color: "#fff",
                              fontSize: "11px",
                              whiteSpace: "nowrap",
                              padding: "3px 8px",
                              borderRadius: "4px",
                              pointerEvents: "none",
                              zIndex: 999,
                            }}
                          >
                            {t.employeeAction || "Employee Action"}
                          </span>
                        )}

                        {hasFeatureAccess && hasFeatureAccess("changesPage", "viewhistory") && (
                          <button
                            style={{
                              ...styles.actionButton,
                              background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                              minWidth: "fit-content",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onHistory(emp);
                            }}
                            onMouseEnter={() =>
                              setHoveredActionBtn(`history_${emp.infinityCode}`)
                            }
                            onMouseLeave={() => setHoveredActionBtn(null)}
                          >
                            <History size={16} />
                          </button>
                        )}
                        {hoveredActionBtn === `history_${emp.infinityCode}` && (
                          <span
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              marginTop: "4px",
                              background: "rgba(0,0,0,0.75)",
                              color: "#fff",
                              fontSize: "11px",
                              whiteSpace: "nowrap",
                              padding: "3px 8px",
                              borderRadius: "4px",
                              pointerEvents: "none",
                              zIndex: 999,
                            }}
                          >
                            {t.employeeHistory || "Employee History"}
                          </span>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div style={{ display: "flex", justifyContent: "center", padding: "20px 0 4px" }}>
                <button
                  style={styles.primaryButton || styles.actionButton}
                  onClick={() => setVisibleCount((prev) => Math.min(prev + CHANGES_PAGE_SIZE, filteredEmployees.length))}
                  className="enhanced-button-hover"
                >
                  {(t.showMore || (isRTL ? "عرض المزيد" : "Show more"))}
                  {` (${pagedEmployees.length}/${filteredEmployees.length})`}
                </button>
              </div>
            )}
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <Users size={100} />
              </div>
              <h3 style={styles.emptyStateTitle}>
                {isLoading ? t.loadingEmployees : t.noEmployeesFound}
              </h3>
              <p style={styles.emptyStateText}>
                {isLoading ? t.pleaseWait : t.noEmployeesMatch}
              </p>
              {!isLoading && (
                <button
                  style={styles.primaryButton}
                  onClick={onClearFilters}
                  className="enhanced-button-hover"
                >
                  <RefreshCw size={18} /> {t.clearFilters}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeeTable;















// import React from "react";
// import {
//   Filter,
//   Search,
//   History,
//   Building,
//   Star,
//   UserCheck,
//   BarChart3,
//   Mail,
//   Phone,
//   Code,
//   Zap,
//   Loader,
//   Users,
//   RefreshCw,
// } from "lucide-react";
// import ReusableDropdown from "../../../components/ReusableDropdown";
// import SearchBar from "../../../components/SearchBar";

// const EmployeeTable = ({
//   t,
//   styles,
//   theme,
//   isRTL,
//   language,
//   isLoading,
//   employees,
//   filteredEmployees,
//   clients,
//   departments,
//   performances,
//   statuses,
//   userRole,
//   selectedClient,
//   setSelectedClient,
//   searchTerm,
//   setSearchTerm,
//   filterDepartment,
//   setFilterDepartment,
//   filterPerformance,
//   setFilterPerformance,
//   filterStatus,
//   setFilterStatus,
//   sortBy,
//   setSortBy,
//   sortOrder,
//   setSortOrder,
//   employeeImages,
//   hoveredActionBtn,
//   setHoveredActionBtn,
//   getEmployeeName,
//   getInitials,
//   hasFeatureAccess,
//   onOpenHistory,
//   onAction,
//   onHistory,
//   onClearFilters,
// }) => {
//   return (
//     <>
//       {/* Filter Section */}
//       <div style={styles.filterCard}>
//         <div
//           style={{
//             display: "flex",
//             flexDirection: isRTL ? "row" : "row",
//             alignItems: "center",
//             justifyContent: "space-between",
//             marginBottom: "clamp(22px, 3.5vw, 28px)",
//             paddingBottom: "clamp(18px, 2.8vw, 22px)",
//             borderBottom:
//               theme === "dark"
//                 ? "1px solid rgba(255,255,255,0.15)"
//                 : "1px solid rgba(0,0,0,0.1)",
//             flexWrap: "wrap",
//             gap: "18px",
//           }}
//         >
//           <div>
//             <h3
//               style={{
//                 fontSize: "clamp(18px, 3.2vw, 22px)",
//                 fontWeight: "900",
//                 color: theme === "dark" ? "#ffffff" : "#0f172a",
//                 margin: "0 0 8px 0",
//                 letterSpacing: "-0.02em",
//                 display: "flex",
//                 flexDirection: isRTL ? "row" : "row",
//                 alignItems: "center",
//                 gap: "clamp(10px, 1.5vw, 12px)",
//               }}
//             >
//               <Filter size={window.innerWidth < 768 ? 20 : 22} />
//               {t.searchFilterEmployees}
//             </h3>
//             <p
//               style={{
//                 fontSize: "clamp(12px, 1.7vw, 14px)",
//                 color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
//                 margin: 0,
//                 fontWeight: "600",
//                 textAlign: isRTL ? "right" : "left",
//               }}
//             >
//               {t.showing} {filteredEmployees.length} {t.of} {employees.length}{" "}
//               {t.employees}
//               {(userRole === "Admin" || userRole === "admin") &&
//                 selectedClient !== "all" &&
//                 ` ${t.forSelectedClient}`}
//             </p>
//           </div>
//           <div
//             style={{
//               display: "flex",
//               flexDirection: isRTL ? "row" : "row",
//               gap: "14px",
//               alignItems: "center",
//               flexWrap: "wrap",
//             }}
//           >
//             {hasFeatureAccess && hasFeatureAccess("changesPage", "viewhistory") && (
//               <button
//                 style={styles.secondaryButton}
//                 onClick={onOpenHistory}
//               >
//                 <History size={18} /> {t.viewHistory}
//               </button>
//             )}
//           </div>
//         </div>

//         <div style={styles.controlsGrid}>
//           <div style={styles.controlGroup}>
//             <label style={styles.label}>
//               <Search size={15} />
//               {t.searchEmployee}
//             </label>
//             <SearchBar
//               value={searchTerm}
//               onChange={setSearchTerm}
//               placeholder={t.searchPlaceholder}
//               theme={theme}
//               language={language}
//               icon={true}
//             />
//           </div>

//           {(userRole === "Admin" || userRole === "admin") && (
//             <div style={styles.controlGroup}>
//               <label style={styles.label}>
//                 <Building size={15} />
//                 {t.client}
//               </label>
//               <div style={{ position: "relative" }}>
//                 <ReusableDropdown
//                   items={[
//                     { id: "all", name: t.allClients || "All" },
//                     ...clients.map((client) => ({
//                       id: client.id,
//                       name: client.clientName,
//                     })),
//                   ]}
//                   value={selectedClient}
//                   onChange={(val) => setSelectedClient(val || "all")}
//                   onSelect={(item) => {
//                     const id =
//                       typeof item === "string"
//                         ? item
//                         : item?.id || String(item);
//                     setSelectedClient(id);
//                   }}
//                   getItemKey={(it) =>
//                     typeof it === "string" ? it : it.id || String(it)
//                   }
//                   getItemLabel={(it) =>
//                     typeof it === "string" ? it : it.name || String(it)
//                   }
//                   placeholder={t.selectClient || "اختر عميل..."}
//                   theme={theme}
//                   language={language}
//                   maxItems={10}
//                   onClear={() => setSelectedClient("all")}
//                 />
//               </div>
//             </div>
//           )}

//           <div style={styles.controlGroup}>
//             <label style={styles.label}>
//               <Building size={15} />
//               {t.department}
//             </label>
//             <div style={{ position: "relative" }}>
//               <ReusableDropdown
//                 items={[
//                   { id: "all", name: t.allDepartments || "All Departments" },
//                   ...departments.map((dept) => ({ id: dept, name: dept })),
//                 ]}
//                 value={filterDepartment}
//                 onChange={(val) => setFilterDepartment(val || "all")}
//                 onSelect={(item) => {
//                   const value =
//                     typeof item === "string" ? item : item?.id || String(item);
//                   setFilterDepartment(value);
//                 }}
//                 getItemKey={(it) =>
//                   typeof it === "string" ? it : it.id || String(it)
//                 }
//                 getItemLabel={(it) =>
//                   typeof it === "string" ? it : it.name || String(it)
//                 }
//                 placeholder={t.selectDepartment || "اختر قسم..."}
//                 theme={theme}
//                 language={language}
//                 maxItems={10}
//                 onClear={() => setFilterDepartment("all")}
//               />
//             </div>
//           </div>

//           <div style={styles.controlGroup}>
//             <label style={styles.label}>
//               <UserCheck size={15} />
//               {t.status}
//             </label>
//             <div style={{ position: "relative" }}>
//               <ReusableDropdown
//                 items={[
//                   { id: "all", name: t.allStatuses || "All" },
//                   ...statuses.map((status) => ({ id: status, name: status })),
//                 ]}
//                 value={filterStatus}
//                 onChange={(val) => setFilterStatus(val || "all")}
//                 onSelect={(item) => {
//                   const value =
//                     typeof item === "string" ? item : item?.id || String(item);
//                   setFilterStatus(value);
//                 }}
//                 getItemKey={(it) =>
//                   typeof it === "string" ? it : it.id || String(it)
//                 }
//                 getItemLabel={(it) =>
//                   typeof it === "string" ? it : it.name || String(it)
//                 }
//                 placeholder={t.selectStatus || "اختر الحالة..."}
//                 theme={theme}
//                 language={language}
//                 maxItems={10}
//                 onClear={() => setFilterStatus("all")}
//               />
//             </div>
//           </div>

//           {/* <div style={styles.controlGroup}>
//             <label style={styles.label}>
//               <BarChart3 size={15} />
//               {t.sortBy}
//             </label>
//             <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
//               <div style={{ flex: 1, position: "relative" }}>
//                 <ReusableDropdown
//                   items={[
//                     { id: "name", name: t.name },
//                     { id: "salary", name: t.salary },
//                     { id: "joinDate", name: t.joinDate },
//                     { id: "performance", name: t.performance },
//                   ]}
//                   value={sortBy}
//                   onChange={(val) => setSortBy(val || "name")}
//                   onSelect={(item) => setSortBy(item.id)}
//                   getItemKey={(it) =>
//                     typeof it === "string" ? it : it.id || String(it)
//                   }
//                   getItemLabel={(it) =>
//                     typeof it === "string" ? it : it.name || String(it)
//                   }
//                   placeholder={t.sortBy}
//                   theme={theme}
//                   language={language}
//                   maxItems={10}
//                   onClear={() => setSortBy("name")}
//                 />
//               </div>
//               <button
//                 style={{
//                   ...styles.select,
//                   fontSize: "clamp(20px, 3vw, 24px)",
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   cursor: "pointer",
//                 }}
//                 onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
//                 title={sortOrder === "asc" ? t.ascendingOrder : t.descendingOrder}
//               >
//                 {sortOrder === "asc" ? "↑" : "↓"}
//               </button>
//             </div>
//           </div> */}

//         </div>
//       </div>

//       {/* Employee Table */}
//       <div style={styles.tableCard}>
//         {isLoading && (
//           <div style={styles.loadingOverlay}>
//             <div style={{ textAlign: "center" }}>
//               <Loader size={48} style={styles.loadingSpinner} color="#2e7cba" />
//               <p
//                 style={{
//                   marginTop: "16px",
//                   fontWeight: "600",
//                   color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
//                 }}
//               >
//                 {t.loadingEmployees}
//               </p>
//             </div>
//           </div>
//         )}

//         <div style={styles.tableWrapper}>
//           {filteredEmployees.length > 0 ? (
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.th}>{t.employee}</th>
//                   <th style={styles.th}>{t.actions}</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {filteredEmployees.map((emp) => (
//                   <tr key={emp.id || emp.infinityCode}>
//                     <td style={styles.td}>
//                       <div style={styles.employeeCell}>
//                         <div
//                           className="employeeAvatar"
//                           style={{
//                             ...styles.employeeAvatar,
//                             position: "relative",
//                             overflow: "hidden",
//                             flexShrink: 0,
//                           }}
//                         >
//                           {employeeImages[emp.infinityCode] && (
//                             <img
//                               src={employeeImages[emp.infinityCode]}
//                               alt={getEmployeeName(emp) || ""}
//                               style={{
//                                 width: "100%",
//                                 height: "100%",
//                                 objectFit: "cover",
//                                 display: "block",
//                                 position: "absolute",
//                                 inset: 0,
//                                 zIndex: 2,
//                               }}
//                               onError={(e) => {
//                                 e.currentTarget.style.display = "none";
//                               }}
//                             />
//                           )}
//                           <div
//                             className="avatar-initials"
//                             style={{
//                               position: "absolute",
//                               inset: 0,
//                               display: "flex",
//                               alignItems: "center",
//                               justifyContent: "center",
//                               zIndex: 1,
//                             }}
//                           >
//                             {getInitials(getEmployeeName(emp), emp.sr)}
//                           </div>
//                         </div>
//                         <div style={styles.employeeInfo}>
//                           <div style={styles.employeeName}>
//                             {getEmployeeName(emp)}
//                           </div>
//                           <div style={styles.employeeDetail}>
//                             <Mail size={13} />
//                             {emp.email || t.notAvailable}
//                           </div>
//                           <div style={styles.employeeDetail}>
//                             <Phone size={13} />
//                             {emp.phoneNumber || t.notAvailable}
//                           </div>
//                           {emp.infinityCode && (
//                             <div style={styles.employeeDetail}>
//                               <Code size={13} />
//                               {emp.infinityCode}
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </td>

//                     <td style={styles.td}>
//                       <div
//                         style={{
//                           display: "flex",
//                           flexDirection: isRTL ? "row" : "row",
//                           gap: "8px",
//                           flexWrap: "nowrap",
//                           alignItems: "center",
//                           position: "relative",
//                         }}
//                       >
//                         <button
//                           style={{
//                             ...styles.actionButton,
//                             background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
//                             minWidth: "fit-content",
//                           }}
//                           onClick={() => onAction(emp)}
//                           onMouseEnter={() => setHoveredActionBtn(emp.infinityCode)}
//                           onMouseLeave={() => setHoveredActionBtn(null)}
//                         >
//                           <Zap size={16} />
//                         </button>
//                         {hoveredActionBtn === emp.infinityCode && (
//                           <span
//                             style={{
//                               position: "absolute",
//                               top: "100%",
//                               left: "50%",
//                               transform: "translateX(-50%)",
//                               marginTop: "4px",
//                               background: "rgba(0,0,0,0.75)",
//                               color: "#fff",
//                               fontSize: "11px",
//                               whiteSpace: "nowrap",
//                               padding: "3px 8px",
//                               borderRadius: "4px",
//                               pointerEvents: "none",
//                               zIndex: 999,
//                             }}
//                           >
//                             {t.employeeAction || "Employee Action"}
//                           </span>
//                         )}

//                         {hasFeatureAccess && hasFeatureAccess("changesPage", "viewhistory") && (
//                           <button
//                             style={{
//                               ...styles.actionButton,
//                               background: "linear-gradient(135deg, #06b6d4, #0891b2)",
//                               minWidth: "fit-content",
//                             }}
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               onHistory(emp);
//                             }}
//                             onMouseEnter={() =>
//                               setHoveredActionBtn(`history_${emp.infinityCode}`)
//                             }
//                             onMouseLeave={() => setHoveredActionBtn(null)}
//                           >
//                             <History size={16} />
//                           </button>
//                         )}
//                         {hoveredActionBtn === `history_${emp.infinityCode}` && (
//                           <span
//                             style={{
//                               position: "absolute",
//                               top: "100%",
//                               left: "50%",
//                               transform: "translateX(-50%)",
//                               marginTop: "4px",
//                               background: "rgba(0,0,0,0.75)",
//                               color: "#fff",
//                               fontSize: "11px",
//                               whiteSpace: "nowrap",
//                               padding: "3px 8px",
//                               borderRadius: "4px",
//                               pointerEvents: "none",
//                               zIndex: 999,
//                             }}
//                           >
//                             {t.employeeHistory || "Employee History"}
//                           </span>
//                         )}
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           ) : (
//             <div style={styles.emptyState}>
//               <div style={styles.emptyStateIcon}>
//                 <Users size={100} />
//               </div>
//               <h3 style={styles.emptyStateTitle}>
//                 {isLoading ? t.loadingEmployees : t.noEmployeesFound}
//               </h3>
//               <p style={styles.emptyStateText}>
//                 {isLoading ? t.pleaseWait : t.noEmployeesMatch}
//               </p>
//               {!isLoading && (
//                 <button
//                   style={styles.primaryButton}
//                   onClick={onClearFilters}
//                   className="enhanced-button-hover"
//                 >
//                   <RefreshCw size={18} /> {t.clearFilters}
//                 </button>
//               )}
//             </div>
//           )}
//         </div>
//       </div>
//     </>
//   );
// };

// export default EmployeeTable;